import { CircleSpinner } from "@@/shared/spinners/circle-spinner";
import type { TextBoxProps } from "@@/shared/text";
import { TextBox } from "@@/shared/text";
import type { WithQueryOptions } from "@@/shared/with-query";
import { useTranslate } from "@@/translations/use-translate";
import { useUserReference } from "@@/users/state/user-reference-fetchers";
import {
    type ContactInfo,
    getUserDisplayName,
    isCustomerId,
    isUserId,
} from "@towni/common";
import type * as React from "react";
import type { Except } from "type-fest";

type Props = Except<TextBoxProps, "text" | "children"> & {
    customerId: string | undefined;
    showFullName?: boolean;
};

const UserDisplayNameTextBox = (props: Props) => {
    const { customerId, ...textBoxProps } = props;
    const [userReference] = useUserReference(customerId);

    const displayName = getUserDisplayName(userReference, {
        showFullName: props.showFullName,
    });

    return (
        <TextBox {...textBoxProps} text={displayName} spin={!userReference} />
    );
};
const ContactInfoDisplayNameTextBox = (
    props: Except<TextBoxProps, "text" | "children"> & {
        contactInfo: ContactInfo;
    } & {
        options?: {
            showFullName?: boolean;
            showPhoneNumberInsteadOfAnonymous?: boolean;
        };
    },
) => {
    const { contactInfo, ...textBoxProps } = props;

    const displayName = getUserDisplayName(contactInfo, props.options);

    return <TextBox {...textBoxProps} text={displayName} spin={!contactInfo} />;
};

const WithUserDisplayName = (
    props: Props & {
        options?: WithQueryOptions<string>;
        children: (userDisplayName: string) => React.ReactNode;
    },
) => {
    const { customerId } = props;
    const translate = useTranslate();
    const [userReference, query] = useUserReference(customerId);
    if (!(isUserId(customerId) || isCustomerId(customerId))) {
        if (props.options?.whenNotFound) return props.options?.whenNotFound;
        throw new Error("missing customer id");
    }
    if (query.isLoading) {
        if (props.options?.whenLoading) return props.options?.whenLoading;
        return <CircleSpinner />;
    }
    if (!userReference) {
        if (props.options?.whenNotFound) return props.options?.whenNotFound;
        return <CircleSpinner />;
    }

    const displayName = translate(getUserDisplayName(userReference));
    return <>{props.children(displayName)}</>;
};

export {
    ContactInfoDisplayNameTextBox,
    UserDisplayNameTextBox,
    WithUserDisplayName,
    getUserDisplayName,
};
